import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, User, Shield, Calendar, Eye } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import usuariosService from '../services/usuariosService'
import { formatearFechaParaMostrar } from '../utils/dateUtils'
import LoadingSpinner from '../components/LoadingSpinner'

const UsuarioDetalle = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { hasPermission } = useAuth()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [usuario, setUsuario] = useState(null)

  useEffect(() => {
    cargarUsuario()
  }, [id])

  const cargarUsuario = async () => {
    try {
      setLoading(true)
      const response = await usuariosService.getUsuario(id)
      setUsuario(response.data)
      setError(null)
    } catch (error) {
      console.error('Error cargando usuario:', error)
      setError('Error al cargar el usuario')
    } finally {
      setLoading(false)
    }
  }

  const getPermisosCategorizados = (permisos) => {
    const categorias = {
      'Gestión de Usuarios': [
        'VER_USUARIOS',
        'AGREGAR_USUARIO',
        'EDITAR_USUARIO',
        'ELIMINAR_USUARIO'
      ],
      'Gestión de Clientes': [
        'VER_CLIENTES',
        'AGREGAR_CLIENTE',
        'EDITAR_CLIENTE',
        'ELIMINAR_CLIENTE',
        'CAMBIAR_DEUDA'
      ],
      'Simulaciones GPS': [
        'VER_SIMULACIONES_GPS',
        'AGREGAR_SIMULACION_GPS',
        'EDITAR_SIMULACION_GPS',
        'ELIMINAR_SIMULACION_GPS',
        'DUPLICAR_SIMULACION_GPS',
        'NUEVA_SIMULACION',
        'COPIAR_SIMULACION',
        'EDITAR_SIMULACION',
        'ELIMINAR_SIMULACION'
      ],
      'Réplicas GPS': [
        'VER_REPLICAS_GPS',
        'COPIAR_REPLICAS',
        'EDITAR_REPLICAS',
        'ELIMINAR_REPLICAS',
        'VER_REPLICAS',
        'ACTUALIZAR_FECHAS_GPS'
      ],
      'Herramientas GPS': [
        'SIMULAR_POSICION_GPS',
        'SIMULAR_POSICION',
        'GENERAR_RUTA_GPS',
        'GENERAR_RUTA'
      ]
    }

    const permisosCategorizados = {}
    permisos.forEach(permiso => {
      for (const [categoria, permisosCategoria] of Object.entries(categorias)) {
        if (permisosCategoria.includes(permiso)) {
          if (!permisosCategorizados[categoria]) {
            permisosCategorizados[categoria] = []
          }
          permisosCategorizados[categoria].push(permiso)
          break
        }
      }
    })

    return permisosCategorizados
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        </div>
      </div>
    )
  }

  if (!usuario) {
    return (
      <div className="p-6">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          Usuario no encontrado
        </div>
      </div>
    )
  }

  const permisosCategorizados = getPermisosCategorizados(usuario.permisos || [])

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <User className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Detalles del Usuario</h1>
            <p className="text-gray-600">Información completa del usuario</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/usuarios')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          
          {hasPermission('EDITAR_USUARIO') && (
            <button
              onClick={() => navigate(`/usuarios/editar/${usuario.id}`)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Edit className="w-5 h-5" />
              Editar
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información básica */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Información Básica
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    ID del Usuario
                  </label>
                  <p className="text-sm font-semibold text-gray-900">
                    {usuario.id}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Nombre de Usuario
                  </label>
                  <p className="text-sm font-semibold text-gray-900">
                    {usuario.usuario}
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Fecha de Creación
                </label>
                <p className="text-sm text-gray-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatearFechaParaMostrar(usuario.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Permisos */}
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Permisos del Usuario
            </h2>
            
            {Object.keys(permisosCategorizados).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(permisosCategorizados).map(([categoria, permisos]) => (
                  <div key={categoria} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">{categoria}</h3>
                    <div className="flex flex-wrap gap-2">
                      {permisos.map((permiso) => (
                        <span
                          key={permiso}
                          className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full flex items-center gap-1"
                        >
                          <CheckCircle className="w-3 h-3" />
                          {permiso}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Este usuario no tiene permisos asignados</p>
              </div>
            )}
          </div>
        </div>

        {/* Panel lateral */}
        <div className="space-y-6">
          {/* Estadísticas */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Estadísticas</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total de Permisos</span>
                <span className="text-lg font-semibold text-blue-600">
                  {usuario.permisos ? usuario.permisos.length : 0}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Categorías</span>
                <span className="text-lg font-semibold text-green-600">
                  {Object.keys(permisosCategorizados).length}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Estado</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  Activo
                </span>
              </div>
            </div>
          </div>

          {/* Acciones rápidas */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones</h3>
            
            <div className="space-y-2">
              {hasPermission('EDITAR_USUARIO') && (
                <button
                  onClick={() => navigate(`/usuarios/editar/${usuario.id}`)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Editar Usuario
                </button>
              )}
              
              <button
                onClick={() => navigate('/usuarios')}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al Listado
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UsuarioDetalle 